import React, { useEffect, useState } from "react";
import { Button, StatusIndicator } from "@amzn/awsui-components-react";
import { useQuery } from "@tanstack/react-query";
import { useOmsIdentity } from "../../hooks/omsContextIdentity";
import { OmsReport, Stage } from "../../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { previousOmsExport } from "../../hooks/useOmsExport";

export default function OMSPersonalExportLink({
  report,
  open,
  onOpen,
}: {
  report: OmsReport;
  open: boolean;
  onOpen: () => void;
}) {
  const { namespace, financialAccount } = useOmsIdentity(report.product);
  const previous = previousOmsExport(report.product, report, namespace);
  const [now, setNow] = useState(Date.now);
  const job = useQuery({
    queryKey: [
      "oms-export",
      namespace,
      previous?.report.snapshotId,
      previous?.mode,
      previous?.jobId,
    ],
    enabled: Boolean(previous) && !open,
    staleTime: 3000,
    retry: false,
    queryFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExport(
          report.product,
          Stage.Prod,
          previous?.jobId as string,
          ...(financialAccount ? ([financialAccount] as const) : ([] as const))
        )
      ).data,
    refetchInterval: (data, query) =>
      !query.state.error && data?.status === "PREPARING" ? 3000 : false,
  });
  useEffect(() => {
    const expiry = Date.parse(job.data?.expiresAt || "");
    const check = () => setNow(Date.now());
    const timer = Number.isFinite(expiry)
      ? window.setTimeout(check, Math.max(0, expiry - Date.now()))
      : undefined;
    window.addEventListener("focus", check);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", check);
    };
  }, [job.data?.expiresAt]);
  if (
    job.isError ||
    !job.data ||
    !Number.isFinite(Date.parse(job.data.expiresAt)) ||
    Date.parse(job.data.expiresAt) <= now ||
    !["READY", "PREPARING"].includes(job.data.status)
  )
    return null;
  return (
    <div className="oms-report-card__notice">
      <StatusIndicator
        type={job.data.status === "READY" ? "success" : "in-progress"}
      >
        {job.data.status === "READY"
          ? "Your export is ready"
          : "Your export is preparing"}
      </StatusIndicator>
      <Button onClick={onOpen}>Return to export</Button>
    </div>
  );
}
